import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@tinct/web-components';
import { updateLeadFormSchema, type UpdateLeadForm } from '@tinct/core-api-models';
import { LeadFormEditor } from './lead-form-editor';

export type LeadFormDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  value: UpdateLeadForm;
  integrationsUrl: string;
  onSave: (value: UpdateLeadForm) => void;
};

/**
 * Dialog wrapper around the editor for the funnel's "Add a lead form"
 * control — the draft only leaves the dialog on a valid Save.
 */
export function LeadFormDialog({
  open,
  onOpenChange,
  value,
  integrationsUrl,
  onSave,
}: LeadFormDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
        {/* Radix unmounts the content on close, so the draft resets from
            `value` on every open. */}
        <LeadFormDialogForm
          value={value}
          integrationsUrl={integrationsUrl}
          onSave={onSave}
          onCancel={() => onOpenChange(false)}
        />
      </DialogContent>
    </Dialog>
  );
}

type LeadFormDialogFormProps = {
  value: UpdateLeadForm;
  integrationsUrl: string;
  onSave: (value: UpdateLeadForm) => void;
  onCancel: () => void;
};

function LeadFormDialogForm({
  value,
  integrationsUrl,
  onSave,
  onCancel,
}: LeadFormDialogFormProps) {
  const [draft, setDraft] = useState<UpdateLeadForm>(value);
  const [validationError, setValidationError] = useState<string | null>(null);

  const handleSave = () => {
    if (draft.enabled) {
      const parsed = updateLeadFormSchema.safeParse(draft);
      if (!parsed.success) {
        setValidationError(parsed.error.issues[0]?.message ?? 'Invalid form');
        return;
      }
    }
    setValidationError(null);
    onSave(draft);
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle>Lead form</DialogTitle>
      </DialogHeader>

      <LeadFormEditor
        value={draft}
        onChange={setDraft}
        integrationsUrl={integrationsUrl}
      />

      {validationError && (
        <p className="text-sm text-destructive">{validationError}</p>
      )}

      <DialogFooter>
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button onClick={handleSave}>Save</Button>
      </DialogFooter>
    </>
  );
}
